import { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Editor } from 'primereact/editor';
import PropTypes from 'prop-types';

import { TOAST } from 'constant';
import { toast } from 'layout';
import { mapError } from 'utils/func';

const defaultControlValue = {
  mode: 'delete',
  mutate: null,
  value: { id: null, englishName: null, vietnameseName: null },
};

const Confirm = forwardRef(({ refetch }, ref) => {
  // #region Data
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [projectProposalControl, setProjectProposalControl] = useState(defaultControlValue);
  const { mode, mutate, value } = projectProposalControl;
  const [commentApprove, setCommentApprove] = useState('');
  // #endregion

  // #region Event
  useImperativeHandle(
    ref,
    () => ({
      open: (_projectProposalControl) => {
        setProjectProposalControl({ ..._projectProposalControl });
        setVisible(true);
      },
    }),
    []
  );

  const footerConfirmationSubmit = () =>
    mode !== 'approve' ? (
      <div>
        <Button
          label={t('pubProjectProposal.cancel')}
          className="w-7rem"
          severity="danger"
          onClick={() => {
            setProjectProposalControl(defaultControlValue);
            setVisible(false);
          }}
          autoFocus
        />
        <Button
          label={t('pubProjectProposal.ok')}
          className="w-7rem"
          severity="info"
          onClick={() => {
            if (!value.id) return;
            mutate(value.id, {
              onSuccess: () => {
                toast(TOAST.SUCCESS, t(`pubProjectProposal.${mode}.success`));
                setProjectProposalControl(defaultControlValue);
                setVisible(false);
                refetch();
              },
              onError: (error) => {
                toast(TOAST.ERROR, mapError(t, error));
              },
            });
          }}
        />
      </div>
    ) : (
      <div>
        <Button
          label={t('pubProjectProposal.approve.requestChange')}
          className="w-20rem"
          severity="warning"
          onClick={() => {
            if (!value.id) return;
            mutate(
              {
                id: value?.id,
                status: 'pendingChange',
                comment: commentApprove,
              },
              {
                onSuccess: () => {
                  toast(TOAST.SUCCESS, t(`pubProjectProposal.${mode}.success`));
                  setProjectProposalControl(defaultControlValue);
                  setVisible(false);
                  refetch();
                },
                onError: (error) => {
                  toast(TOAST.ERROR, mapError(t, error));
                },
              }
            );
          }}
        />
        <Button
          label={t('pubProjectProposal.approve.approved')}
          className="w-9rem"
          severity="info"
          onClick={() => {
            if (!value.id) return;
            mutate(
              {
                id: value?.id,
                status: 'approved',
                comment: commentApprove,
              },
              {
                onSuccess: () => {
                  toast(TOAST.SUCCESS, t(`pubProjectProposal.${mode}.success`));
                  setProjectProposalControl(defaultControlValue);
                  setVisible(false);
                  refetch();
                },
                onError: (error) => {
                  toast(TOAST.ERROR, mapError(t, error));
                },
              }
            );
          }}
        />
      </div>
    );
  // #endregion
  return (
    <Dialog
      header={
        // eslint-disable-next-line react/jsx-wrap-multilines
        <p className={`text-${mode === 'delete' ? 'red' : 'yellow'}-500`}>
          {t(`pubProjectProposal.${mode}.title`)}
        </p>
      }
      visible={visible}
      position="center"
      style={{ width: mode === 'approve' ? '600px' : '400px' }}
      onHide={() => {
        setProjectProposalControl(defaultControlValue);
        setVisible(false);
      }}
      draggable={false}
      footer={footerConfirmationSubmit}
    >
      <p className="text-lg font-bold">
        {t('pubProjectProposal.label')}:{' '}
        {t('lang') === 'vi' ? value?.vietnameseName : value?.englishName}
      </p>
      <p className="text-lg">{t(`pubProjectProposal.${mode}.confirmationMessage`)}</p>
      {mode === 'approve' && (
        <Editor
          id="comment"
          style={{ height: '100px' }}
          className="w-full"
          tooltip={t('pubProjectProposal.approve.comment')}
          tooltipOptions={{ position: 'bottom' }}
          onTextChange={(e) => {
            const htmlValue = (e.htmlValue ?? '')?.replace(/<[^>]+>(<br>)+<\/[^>]+>/g, '');
            setCommentApprove(htmlValue);
          }}
          placeholder={t('pubProjectProposal.approve.comment')}
        />
      )}
    </Dialog>
  );
});

export default Confirm;

Confirm.propTypes = {
  refetch: PropTypes.func.isRequired,
};
